import { appService } from "../App/app.service";
import { store } from "../util/store";

/**
 * Fetches the team hierarchy and returns first-level team names.
 * Uses getGenericScoreCardFilters as the standard source for team hierarchy.
 * 
 * Team data is stored at the TOP LEVEL of Redux state (not inside state.user)
 * so it is never persisted to localStorage and always re-fetches fresh on page refresh.
 * 
 * @returns {Promise<Array>} Promise resolving to a list of first-level team names.
 */
export const fetchFirstLevelTeamNames = () => {
  const appState = store.getState()?.app || {};
  if (appState.rccFirstLevelTeamsLoaded) {
    return Promise.resolve(
      Array.isArray(appState.rccFirstLevelTeams) ? appState.rccFirstLevelTeams : []
    );
  }

  return appService
    .getRccTeams(1)
    .then((resp) => {
      const teamNames = Array.isArray(resp?.data) ? resp.data : [];
      store.dispatch({ type: "set_rcc_first_level_teams", teams: teamNames });
      return teamNames;
    })
    .catch((error) => {
      console.error("Error fetching team hierarchy:", error);
      store.dispatch({ type: "set_rcc_first_level_teams", teams: [] });
      return [];
    });
};

/**
 * Fetches the default team filter from performance scorecard filters.
 * Inputs: none. Outputs: array of team names (empty if not set or "All").
 *
 * Team data is stored at the TOP LEVEL of Redux state (not inside state.user)
 * so it is never persisted to localStorage and always re-fetches fresh on page refresh.
 *
 * @returns {Promise<Array>} Promise resolving to default team list.
 */
export const fetchDefaultTeamFilter = () => {
  const appState = store.getState()?.app || {};
  if (appState.rccDefaultTeamFilterLoaded) {
    return Promise.resolve(
      Array.isArray(appState.rccDefaultTeamFilter) ? appState.rccDefaultTeamFilter : []
    );
  }

  return appService
    .getGenericScoreCardFilters()
    .then((resp) => {
      const message = resp?.message || {};
      const defaultTeam =
        message?.parentChildrenTeamFilter?.defaultValueTeam ||
        message?.parentChildrenTeamFilter?.parentChildrenTeamFilter
          ?.defaultValueTeam ||
        [];

      const teamList = Array.isArray(defaultTeam) ? defaultTeam.filter(Boolean) : [];
      const firstTeam = String(teamList[0] ?? "").toLowerCase();
      if (!firstTeam || firstTeam === "all") {
        store.dispatch({ type: "set_rcc_default_team_filter", teams: [] });
        return [];
      }

      store.dispatch({ type: "set_rcc_default_team_filter", teams: teamList });
      return teamList;
    })
    .catch((error) => {
      console.error("Error fetching default team filter:", error);
      store.dispatch({ type: "set_rcc_default_team_filter", teams: [] });
      return [];
    });
};

/**
 * Fetches the preferred data period and current period for the user's company.
 *
 * @returns {Promise<{ period: "monthly" | "quarterly", currentPeriod: string }>}
 */
export const fetchCompanyPreferredDataPeriod = () => {
  return appService
    .getRccCompanyPreferredDataPeriod()
    .then((resp) => {
      const period = resp?.data?.period === "monthly" ? "monthly" : "quarterly";
      const currentPeriod =
        typeof resp?.data?.currentPeriod === "string" ? resp.data.currentPeriod : "";
      return { period, currentPeriod };
    })
    .catch((error) => {
      console.error("Error fetching company preferred data period:", error);
      return { period: "quarterly", currentPeriod: "" };
    });
};
